// The confirmatory result, rendered only from the validated summary and only
// when the release gate is open. Same layout regardless of the result's sign.

import { interval, points } from "../data/format";
import type { ExperimentSummary, SiteStatus } from "../data/schema";
import { claimHeadline } from "../data/validate";

export function renderResultPanel(
  mount: HTMLElement,
  status: SiteStatus,
  summary: ExperimentSummary,
): void {
  if (status.phase !== "results" || !status.result_release) {
    mount.innerHTML = "";
    const note = document.createElement("p");
    note.className = "muted";
    note.textContent =
      "The experiment is planned, but no results have been released yet.";
    mount.append(note);
    return;
  }

  const primary = summary.primary_summary;
  const budget = summary.budget.additional_revealed_targets.recovery;
  const panel = document.createElement("div");
  panel.className = "result-panel";
  panel.innerHTML = `
    <p class="headline">
      Recovery labels versus extra demonstrations
      <span class="delta"></span>
      <span class="small muted">Difference in task success, in percentage points</span>
    </p>
    <p class="prose">
      <span data-slot="headline"></span>
      The 95% interval is <b data-slot="interval"></b>. This estimate comes from
      <b data-slot="bundles"></b> independent training runs, each tested on the same
      <b data-slot="eligible"></b> scenarios with the new action change.
    </p>
    <details class="technical-details">
      <summary>How this result was measured</summary>
      <p>Each method received <span data-slot="budget"></span> additional labels per run.
      We compared success rates within each run and averaged the differences.
      Every assigned episode counts, including those that ended before the action change.</p>
      <p>The interval uses a paired <i>t</i> analysis. A second calculation using
      a cluster bootstrap gives <span data-slot="sensitivity"></span>.
      The threshold chosen before testing was <span data-slot="sesoi"></span>.
      Here, “pp” means percentage points.</p>
    </details>
  `;
  const q = (name: string) =>
    panel.querySelector<HTMLElement>(`[data-slot="${name}"]`)!;
  panel.querySelector<HTMLElement>(".delta")!.textContent = points(
    primary.mean_paired_difference,
  );
  q("headline").textContent = claimHeadline(summary);
  q("interval").textContent = interval(
    primary.interval.lower,
    primary.interval.upper,
  );
  q("bundles").textContent = String(primary.pipeline_replicates);
  q("eligible").textContent = String(summary.eligibility.eligible_scenarios);
  q("budget").textContent = String(budget);
  q("sensitivity").textContent = interval(
    primary.sensitivity.lower,
    primary.sensitivity.upper,
  );
  q("sesoi").textContent = points(summary.protocol.sesoi_absolute_success);
  mount.replaceChildren(panel);
}
